import { getPaymentMethodIcon, useIsFocused } from '@sg/garnish';
import { CardExpirationState } from '@sg/graphql-schema';

import { useSavedPaymentMethodsQuery } from './graphql/SavedPaymentMethods.generated';

/**
 * Returns the saved payment methods for the current user.
 * Refresh on focus so that returning from the Stripe modal updates the list.
 */
export const useSavedPaymentMethods = () => {
  const isFocused = useIsFocused();

  const [response, refetchPaymentMethods] = useSavedPaymentMethodsQuery({
    requestPolicy: 'cache-and-network',
    pause: !isFocused,
  });

  const paymentMethods = preparePaymentMethodCards(response?.data?.paymentMethodCards);
  const isFetchingPaymentMethods = response.fetching;

  return {
    refetchPaymentMethods,
    paymentMethods,
    isFetchingPaymentMethods,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function preparePaymentMethodCards(paymentMethods?: SavedPaymentMethodsResult) {
  if (!paymentMethods) return [];

  return paymentMethods.filter(isValidPaymentMethod).map((paymentMethod) => ({
    id: paymentMethod.id,
    description: paymentMethod.description as string,
    icon: getPaymentMethodIcon(paymentMethod.cardType),
    isDefault: paymentMethod.isDefault,
    isExpired: paymentMethod.expirationState === CardExpirationState.Expired,
    isExpiring: paymentMethod.expirationState === CardExpirationState.Nearing,
  }));
}

function isValidPaymentMethod(
  paymentMethod: PaymentMethod,
): paymentMethod is Extract<PaymentMethod, { __typename: 'PaymentMethodCard' }> {
  return (
    '__typename' in paymentMethod &&
    paymentMethod.__typename === 'PaymentMethodCard' &&
    paymentMethod.description !== null
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type QueryResponse = ReturnType<typeof useSavedPaymentMethodsQuery>;
type SavedPaymentMethodsResult = NonNullable<QueryResponse[0]['data']>['paymentMethodCards'];
type PaymentMethod = SavedPaymentMethodsResult[number];
